import React, { useState, useMemo, useEffect } from 'react';
import { useGAFields, useGAResources } from './hooks';
import { parseQuery, parseSelectedValues, transformFields, filterNonExistingFields } from './utils';
import { Flex } from '@ui-library/misc';
import ResourceSelect from './selectors/resourceSelection';
import QueryEditor from './queryEditor';
import FieldsSelection from './selectors/fieldsSelection';
import {
  defaultQueryBuilderState,
  QueryBuilderContext,
  QueryBuilderState,
} from './queryBuilderContext';
import classes from './style.module.scss';
import { TabsV1 } from '@ui-library/tabs';
import { QUERY_KEYWORD } from './constants';
import { QueryBuilderKeyword, SelectedFields } from './types';

interface QueryBuilderProviderProps {
  value?: string;
  onChange: (query: string) => void;
  disabled?: boolean;
}
const QueryBuilderProvider: React.FC<QueryBuilderProviderProps> = ({
  value,
  onChange,
  disabled,
}) => {
  // States
  const parsedQuery = useMemo(() => parseQuery(value), []);
  const [resource, setResource] = useState(parsedQuery?.from);
  const { fields, loading } = useGAFields(resource);
  const { resources } = useGAResources();
  const [selectedFields, setSelectedFields] = useState<QueryBuilderState['selectedFields']>(
    parsedQuery?.select,
  );
  const [editMode, setEditMode] = useState(false);
  const [inputQuery, setInputQuery] = useState('');
  const [selectionMode, setSelectionMode] = useState<QueryBuilderKeyword>(QUERY_KEYWORD.SELECT);
  const queryFromFields: string = useMemo<string>(
    () => parseSelectedValues({ from: resource, select: selectedFields }, inputQuery),
    [resource, selectedFields, inputQuery],
  );
  // Effects
  useEffect(() => {
    setSelectedFields(filterNonExistingFields(selectedFields, fields!));
  }, [fields]);
  useEffect(() => {
    if (value && fields) {
      setSelectedFields(parseQuery(value, fields).select);
    }
  }, [!!fields]);
  useEffect(() => {
    setInputQuery(queryFromFields);
    if (fields) {
      onChange(queryFromFields);
    }
  }, [queryFromFields]);

  // Callbacks
  const toggleEditMode = React.useCallback(() => {
    setEditMode(!editMode);
  }, [editMode]);

  const onChangeTextInput = React.useCallback(
    (val: string) => {
      const fieldsFromQuery = parseQuery(val, fields);
      setSelectedFields(fieldsFromQuery.select);
      setInputQuery(val);
    },
    [fields],
  );

  const onChangeResource = React.useCallback(
    (resourceValue: string) => {
      setResource(resourceValue);
    },
    [fields],
  );

  const onChangeFields = React.useCallback(
    (fieldsValue: SelectedFields) => {
      setSelectedFields(fieldsValue);
    },
    [fields, queryFromFields],
  );

  const reset = React.useCallback(() => {
    setSelectedFields(defaultQueryBuilderState.selectedFields);
    setInputQuery('');
  }, [fields, queryFromFields]);
  const renderTab = React.useMemo(() => {
    switch (selectionMode) {
      case QUERY_KEYWORD.SELECT:
        return <FieldsSelection disabled={disabled} onChange={onChangeFields} />;
      case QUERY_KEYWORD.WHERE:
      case QUERY_KEYWORD.LIMIT:
      case QUERY_KEYWORD.ORDER_BY:
      case QUERY_KEYWORD.PARAMETERS:
        return null;
    }
    return null;
  }, [selectionMode, onChangeFields]);

  // Shared state
  const parsedFields = useMemo(
    () => transformFields(fields, selectedFields),
    [fields, selectedFields],
  );
  const sharedState = useMemo(
    () => ({
      loading,
      fields: parsedFields,
      resource,
      selectedFields,
      editMode,
      toggleEditMode,
      resources,
      query: queryFromFields,
      inputQuery,
      selectionMode,
      reset,
    }),
    [
      loading,
      resource,
      parsedFields,
      selectedFields,
      editMode,
      resources,
      inputQuery,
      value,
      selectionMode,
    ],
  );
  return (
    <QueryBuilderContext.Provider value={sharedState}>
      <Flex flexDirection="column" className={classes.queryBuilder}>
        <Flex alignItems="center">
          <div>Select resource</div>
          <div className={classes.resourceSelectWrapper}>
            <ResourceSelect disabled={disabled} onChange={onChangeResource} />
          </div>
        </Flex>
        <QueryEditor disabled={disabled} onChange={onChangeTextInput} />
        <TabsV1
          activeKey={selectionMode}
          onChange={(val) => setSelectionMode(val as QueryBuilderKeyword)}
          items={[{ title: QUERY_KEYWORD.SELECT, key: QUERY_KEYWORD.SELECT }]}
          content={renderTab}
        />
      </Flex>
    </QueryBuilderContext.Provider>
  );
};

export default QueryBuilderProvider;
